import { Cancel, Close } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import * as yup from "yup";
import { ToastContent } from "../../../../../shared/components";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../../../shared/forms";
import { useToast } from "../../../../../shared/hooks";
import { ComissionServices } from "../../../../../shared/services/api/comission/ComissionServices";
import { globalFormatCurrencyBRL } from "../../../../../shared/utils";
import { returnMessageError } from "../../../../../shared/utils/returnMessageError";

interface IComission {
	id: string;
	type: string;
	value: string; // Usamos 'value' para o montante
	description: string;
}

interface IFormComissionCancel {
	obs: string;
}

interface ModalCancelComissionsProps {
	isOpenModal: boolean;
	isClose: () => void;
	isSuperAdmin: boolean;
	row?: IComission;
	rows?: IComission[];
	onSuccess?: () => void;
}

export const ModalCancelComissions = ({ isOpenModal, isClose, isSuperAdmin, row, rows, onSuccess }: ModalCancelComissionsProps) => {
	const { formRef: formRefComission } = useVForm();
	const toastHandler = useToast();
	const [isLoading, setIsLoading] = useState(false);

	const formValidationSchemaCancelComission: yup.SchemaOf<IFormComissionCancel> = yup.object().shape({
		obs: yup.string().required("Campo obrigatório"),
	});

	const formatType = (type: string) => {
		const lowerType = type.toLowerCase();
		if (lowerType === "prize") return "Premiação";
		if (lowerType === "bonus") return "Bônus";
		return lowerType;
	};

	const handleSaveComissionCancel = async (dados: IFormComissionCancel) => {
		try {
			const validated = await formValidationSchemaCancelComission.validate(dados, {
				abortEarly: false,
			});

			const ids = row ? [row.id] : rows?.map((r) => r.id) ?? [];
			if (ids.length === 0) return;

			setIsLoading(true);
			let successCount = 0;

			for (const id of ids) {
				const result = await ComissionServices.updateValuesComission(id, {
					status: "canceled",
					reason: validated.obs,
					cancelDate: new Date().toISOString(),
				});
				if (!(result instanceof Error)) {
					successCount++;
				}
			}

			setIsLoading(false);

			if (successCount > 0) {
				toastHandler.present({
					type: "success",
					position: "top-right",
					messageOrContent: (
						<ToastContent
							title="Sucesso"
							content={successCount === 1 ? "registro cancelado com sucesso." : `${successCount} registros cancelados com sucesso.`}
						/>
					),
				});
				isClose();
				onSuccess?.();
			} else {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content="Não foi possível cancelar os registros." />,
				});
			}
		} catch (errors) {
			setIsLoading(false);
			const validationErros: IVFormErros = {};
			if (errors instanceof yup.ValidationError) {
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRefComission.current?.setErrors(validationErros); // Renomeado formRef
			}
			const { titleMessage, contentMessage } = returnMessageError(errors);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		}
	};

	// Título e info do valor adaptados para COMISSÃO
	const title = rows && rows.length > 1 ? "Deseja cancelar os registros selecionados?" : "Deseja cancelar o registro?";

	let valueInfo = "";
	if (row) {
		valueInfo = `${formatType(row.type)} - ${globalFormatCurrencyBRL(row.value)}`;
	} else if (rows && rows.length === 1) {
		valueInfo = `${formatType(rows[0].type)} - ${globalFormatCurrencyBRL(rows[0].value)}`;
	} else {
		valueInfo = `${rows?.length} registros selecionados`;
	}

	return (
		<Dialog open={isOpenModal} onClose={isClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
				<Typography variant="h6" fontWeight={400} color="primary.main">
					{title}
				</Typography>
				<IconButton onClick={isClose}>
					<Close />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{ px: 4, pt: 1 }}>
				<Typography variant="body2" color="text.secondary" mb={2}>
					{valueInfo}
				</Typography>

				<VForm
					ref={formRefComission} // Renomeado formRef
					onSubmit={handleSaveComissionCancel}
					onPointerEnterCapture={() => {}}
					onPointerLeaveCapture={() => {}}
					placeholder=""
				>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<VTextField name="obs" label="Motivo do cancelamento" fullWidth multiline rows={3} />
						</Grid>
					</Grid>
				</VForm>
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
				<Button variant="outlined" onClick={isClose} disabled={isLoading}>
					Voltar
				</Button>
				<Button variant="contained" color="primary" startIcon={<Cancel />} onClick={() => formRefComission.current?.submitForm()} disabled={isLoading}>
					Confirmar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
